import { formatDistanceToNow } from 'date-fns';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type {
  GetSubmissionAttemptDetailResponse,
} from '@/services/api/agentflowService';

const STATUS_DOT: Record<string, string> = {
  ready: 'bg-green-500',
  blocked: 'bg-amber-500',
  needs_rebuild: 'bg-amber-500',
  needs_re_evaluation: 'bg-amber-500',
  failed: 'bg-red-500',
  draft: 'bg-zinc-400',
  submitting: 'bg-blue-500',
  submitted: 'bg-green-500',
};

const STATUS_BADGE: Record<
  string,
  { label: string; className: string } | undefined
> = {
  ready: {
    label: 'READY',
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  blocked: {
    label: 'BLOCKED',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  needs_rebuild: {
    label: 'BLOCKED',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  needs_re_evaluation: {
    label: 'BLOCKED',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  failed: {
    label: 'FAILED',
    className: 'bg-red-50 text-red-700 border-red-200',
  },
  submitting: {
    label: 'SUBMITTING',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  submitted: {
    label: 'SUBMITTED',
    className: 'bg-green-50 text-green-700 border-green-200',
  },
};

function getSubtitle(detail: GetSubmissionAttemptDetailResponse): string {
  const parts: string[] = [];
  const dest = detail.destination;
  if (dest?.recommended?.provider_name) {
    parts.push(dest.recommended.provider_name);
  } else if (dest?.manual_resolution?.provider_name) {
    parts.push(dest.manual_resolution.provider_name);
  }
  return parts.join(' · ');
}

interface SubmissionAttemptHeaderProps {
  detail: GetSubmissionAttemptDetailResponse;
  onClose: () => void;
}

export const SubmissionAttemptHeader = ({
  detail,
  onClose,
}: SubmissionAttemptHeaderProps) => {
  const badge = STATUS_BADGE[detail.status];
  const completedAgo = detail.completed_at
    ? formatDistanceToNow(new Date(detail.completed_at), { addSuffix: false })
    : null;
  const subtitle = getSubtitle(detail);

  return (
    <div className="flex items-center justify-between gap-4 border-b border-zinc-200 px-6 py-3 shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={cn(
            'inline-block h-2.5 w-2.5 rounded-full shrink-0',
            STATUS_DOT[detail.status] ?? 'bg-zinc-400',
          )}
        />
        <DialogTitle className="text-base font-semibold text-zinc-900 truncate">
          Submission Attempt
        </DialogTitle>
        {subtitle && (
          <span className="text-sm text-zinc-500 truncate">{subtitle}</span>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {badge && (
          <span
            className={cn(
              'inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold tracking-wider',
              badge.className,
            )}
          >
            {badge.label}
          </span>
        )}
        {completedAgo && (
          <span className="text-xs text-zinc-500">{completedAgo}</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
